import { useEffect, useRef, useState } from 'react'
import { Input } from 'antd'
import { Icon } from '@iconify/react'

import AutoHideTooltip from './AutoHideTooltip'
import styles from './StoryMapBoardToolbar.module.css'

const StoryMapBoardSearch = ({ value = '', onChange }) => {
    const [searchDraft, setSearchDraft] = useState(value)
    const [isSearchExpanded, setIsSearchExpanded] = useState(Boolean(value?.trim()))
    const searchWrapperRef = useRef(null)
    const searchInputRef = useRef(null)
    const searchDebounceRef = useRef(null)

    useEffect(() => {
        setSearchDraft(value ?? '')
        if (value?.trim()) {
            setIsSearchExpanded(true)
        }
    }, [value])

    useEffect(() => {
        if (!isSearchExpanded) return undefined

        const frame = window.requestAnimationFrame(() => {
            const input =
                searchInputRef.current?.input || searchInputRef.current
            input?.focus()
        })

        return () => window.cancelAnimationFrame(frame)
    }, [isSearchExpanded])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isSearchExpanded &&
                searchWrapperRef.current &&
                !searchWrapperRef.current.contains(event.target) &&
                !String(searchDraft ?? '').trim()
            ) {
                setIsSearchExpanded(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isSearchExpanded, searchDraft])

    useEffect(
        () => () => {
            if (searchDebounceRef.current) {
                window.clearTimeout(searchDebounceRef.current)
            }
        },
        []
    )

    const emitSearch = (nextValue) => {
        onChange?.(nextValue)
    }

    const handleSearchChange = (nextValue) => {
        setSearchDraft(nextValue)

        if (searchDebounceRef.current) {
            window.clearTimeout(searchDebounceRef.current)
        }

        searchDebounceRef.current = window.setTimeout(() => {
            emitSearch(nextValue)
        }, 400)
    }

    const handleSearchIconClick = () => {
        setIsSearchExpanded(true)
    }

    const searchTooltip = value?.trim()
        ? `Searching: "${value.trim()}"`
        : 'Search journeys, steps, and stories'

    return (
        <div
            ref={searchWrapperRef}
            className={`${styles.searchWrapper} ${
                isSearchExpanded ? styles.searchExpanded : ''
            }`}
        >
            {!isSearchExpanded ? (
                <AutoHideTooltip title={searchTooltip} placement="bottom">
                    <button
                        type="button"
                        className={styles.searchIconButton}
                        onClick={handleSearchIconClick}
                        aria-label="Search journeys, steps, and stories"
                    >
                        <Icon
                            icon="mdi:magnify"
                            width={18}
                            height={18}
                            className={styles.searchIcon}
                        />
                    </button>
                </AutoHideTooltip>
            ) : (
                <Input
                    ref={searchInputRef}
                    placeholder="Search journeys, steps, and stories…"
                    allowClear
                    value={searchDraft}
                    onChange={(event) => handleSearchChange(event.target.value)}
                    onClear={() => {
                        setSearchDraft('')
                        emitSearch('')
                    }}
                    className={styles.searchInput}
                    prefix={
                        <Icon
                            icon="mdi:magnify"
                            width={16}
                            height={16}
                            className={styles.searchIcon}
                        />
                    }
                />
            )}
        </div>
    )
}

export default StoryMapBoardSearch
